import { zodResolver } from '@hookform/resolvers/zod'
import { sendGTMEvent } from '@next/third-parties/google'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { gebaseRegisterSchema, RegisterFormType } from './schema'

type UseRegisterFormOptions = {
  onCaptchaReset: () => void
}

/** The signup route's own failure codes → this form's translations (W01). */
const SIGNUP_ERROR_KEYS = {
  invalidRequest: 'register.errors.invalidRequest',
  captchaRequired: 'register.errors.captchaRequired',
  captchaFailed: 'register.errors.captchaFailed',
  serverUnreachable: 'register.errors.serverUnreachable',
  unexpected: 'register.errors.unexpected',
} as const

const useRegisterForm = ({ onCaptchaReset }: UseRegisterFormOptions) => {
  const { t, i18n } = useTranslation()
  const form = useForm<RegisterFormType>({
    resolver: zodResolver(gebaseRegisterSchema(t)),
    defaultValues: RegisterFormDefaultValues,
  })

  const onSubmit = useCallback(
    async (data: RegisterFormType) => {
      try {
        sendGTMEvent({ event: 'signup', value: JSON.stringify({ email: data.email }) })
        const response = await fetch('/api/signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...data, locale: i18n.language }),
        })

        const result = (await response.json()) as
          | { success: true }
          | { success: false; error?: string; errorCode?: string }

        if (!result.success) {
          throw new Error(
            result.errorCode && result.errorCode in SIGNUP_ERROR_KEYS
              ? t(SIGNUP_ERROR_KEYS[result.errorCode as keyof typeof SIGNUP_ERROR_KEYS])
              : (result.error ?? t('register.errors.unexpected'))
          )
        }
        toast.success(t('register.success'))
        form.reset()
      } catch (error) {
        if (!(error instanceof Error)) return
        toast.error(error.message)
      } finally {
        form.setValue('captchaToken', '')
        onCaptchaReset()
      }
    },
    [form, onCaptchaReset, t, i18n]
  )

  return { form, handleSubmit: form.handleSubmit(onSubmit) }
}

export default useRegisterForm

const RegisterFormDefaultValues: Partial<RegisterFormType> = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  captchaToken: '',
}
